/**
 * useDocumentHead - Custom hook for managing document title and meta tags
 * Lightweight alternative to react-helmet, fully compatible with React 19
 */

import { useEffect } from 'react';

type MetaTag = {
  name?: string;
  property?: string;
  content: string;
};

type DocumentHeadOptions = {
  title?: string;
  description?: string;
  robots?: string;
  metaTags?: MetaTag[];
};

/**
 * Hook to manage document head elements (title, meta tags)
 * 
 * @example
 * useDocumentHead({
 *   title: 'My Page | bQuest',
 *   description: 'Page description here',
 *   robots: 'noindex, nofollow'
 * });
 */
export const useDocumentHead = (options: DocumentHeadOptions) => {
  useEffect(() => {
    const originalTitle = document.title;
    const addedMetaTags: HTMLMetaElement[] = [];

    // Update title
    if (options.title) {
      document.title = options.title;
    }

    // Helper to create or update meta tag
    const setMetaTag = (selector: string, content: string) => {
      let metaTag = document.querySelector<HTMLMetaElement>(selector);
      
      if (!metaTag) {
        metaTag = document.createElement('meta');
        const [attr, value] = selector.replace('meta[', '').replace(']', '').split('=');
        metaTag.setAttribute(attr, value.replace(/"/g, ''));
        document.head.appendChild(metaTag);
        addedMetaTags.push(metaTag);
      }
      
      metaTag.setAttribute('content', content);
    };

    // Set description
    if (options.description) {
      setMetaTag('meta[name="description"]', options.description);
    }

    // Set robots
    if (options.robots) {
      setMetaTag('meta[name="robots"]', options.robots);
    }

    // Set custom meta tags
    if (options.metaTags) {
      options.metaTags.forEach((tag) => {
        if (tag.name) {
          setMetaTag(`meta[name="${tag.name}"]`, tag.content);
        } else if (tag.property) {
          setMetaTag(`meta[property="${tag.property}"]`, tag.content);
        }
      });
    }

    // Cleanup function
    return () => {
      document.title = originalTitle;
      addedMetaTags.forEach((tag) => tag.remove());
    };
  }, [options.title, options.description, options.robots, options.metaTags]);
};
